import axios from "axios";
import PropTypes from "prop-types";
import { enqueueSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { format, secondsToMilliseconds } from "date-fns";
// @mui
import { Circle, Send } from "@mui/icons-material";
import { alpha, Box, Card, CardActionArea, Chip, CircularProgress, Divider, Grid, IconButton, Stack, TextField, Typography, useTheme } from "@mui/material";
// components
import Page from "src/components/Page";
import useAppContext from "src/hooks/useAppContext";
//
import { WEB_API } from "src/config";
import { toCurrency } from "src/utils/number";

Message.propTypes = {
  message: PropTypes.any,
  isClient: PropTypes.bool
}
function Message({ message, isClient }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 1,
        mb: 1,
        borderRadius: 2,
        width: 'fit-content',
        ml: !isClient ? 0 : 'auto',
        bgcolor: !isClient ? theme.palette.grey[500_48] : alpha(theme.palette.success.light, 0.7),
      }}
    >
      {/* <Typography variant="caption" fontWeight={700}>{message.author}</Typography> */}
      <Typography variant="body2" sx={{ width: 'fit-content', whiteSpace: 'pre-wrap' }}>
        {message.msg}
      </Typography>
      <Stack direction={'row'} alignItems={'center'}>
        <Typography
          variant="caption"
          lineHeight={1}
          fontSize={10}
          // color={theme.palette.grey[200]}
          sx={{ display: 'block', width: '100%', textAlign: 'right' }}
        >
          {format(message.hour, "HH:mm")}
        </Typography>

        {/* {!isClient && (lastReadMessageIndex ?? -1) >= message.index &&
          <DoneAll />
        } */}
      </Stack>
    </Box>
  )
}

SendBar.propTypes = {
  conversation: PropTypes.any,
  setMessages: PropTypes.func,
}
function SendBar({ conversation, setMessages }) {
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const { getIdToken, user } = useAppContext();

  const sendMessage = async (message) => {
    setLoading(true);
    const msg = {
      msg: message,
      createdAt: Date.now(),
      order: conversation.id
    }

    let params = {};
    if (user?.email) {
      const token = await getIdToken();
      params = {
        url: `${WEB_API}/put_message`,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    } else {
      params = {
        url: `${WEB_API}/put_message?user_id=${user.userId}`,
        method: 'POST'
      }
    }

    await axios.request({
      ...params,
      data: msg,
      headers: {
        ...(params.headers || {}),
        'Access-Control-Allow-Origin': '*',
        Accept: '*/*'
      }
    }).then(() => {
      delete msg.token;
      setMessages(prev => ({ ...prev, [conversation.id]: [...(prev[conversation.id] || []), { ...msg, hour: Date.now(), author: 'customer' }] }));
      setLoading(false);
    }).catch(err => {
      console.error(err);
      enqueueSnackbar("Erro ao enviar mensagem. Atualize a página e tente novamente!", { variant: 'error' });
      setLoading(false);
    })
  }

  return (
    <Stack direction={'row'} alignItems={'center'}>
      <TextField
        placeholder="Mensagem"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            sendMessage(e.target.value);
            setBody('');
          }
        }}
        sx={{ width: '100%' }}
      />
      <IconButton
        size='large'
        onClick={() => {
          sendMessage(body);
          setBody('');
        }}
        disabled={!body || loading}
        sx={{ mx: 1, color: 'white', width: 48, height: 48, bgcolor: theme.palette.primary.main }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : <Send />}
      </IconButton>
    </Stack>
  )
}

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);

  const theme = useTheme();
  const { user, lastMessagesUpdate, conversationsReadStatus, setConversationsReadStatus, getIdToken, setLastMessagesPointer } = useAppContext();
  // eslint-disable-next-line
  const [searchParams, setSearchParams] = useSearchParams();
  const simpleBarRef = useRef(null);

  const getActiveConversations = async () => {
    try {
      let params = {};
      if (user?.email) {
        const token = await getIdToken();
        params = {
          url: `${WEB_API}/list_conversations`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      } else {
        params = {
          url: `${WEB_API}/list_conversations?user_id=${user.userId}`,
          method: 'GET',
        }
      }

      // setLoading(true);

      const resp = await axios.request({
        ...params,
        headers: {
          ...(params.headers || {}),
          'Access-Control-Allow-Origin': '*',
          Accept: '*/*'
        }
      })

      const status = {};
      resp.data.forEach(conv => {
        status[conv.id] = conversationsReadStatus?.[conv.id] || false;
      });

      setConversationsReadStatus(status);

      const data = resp.data.sort((a, b) => a.id - b.id).map((e, idx) => (
        {
          ...e,
          unread: !status[e.id],
          label: `Pedido #${idx + 1}`
        }
      ));

      setLastMessagesPointer(Date.now() - secondsToMilliseconds(20));

      return data;
    } catch (e) {
      console.log(e);
      enqueueSnackbar("Erro ao buscar conversas. Atualize a página e tente novamente!", { variant: 'error' });
    }
  }

  const getUnreadMessages = async () => {
    try {
      let params = {};
      if (user?.email) {
        const token = await getIdToken();
        params = {
          url: `${WEB_API}/get_unread_messages/${lastMessagesUpdate.current}`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      } else {
        params = {
          url: `${WEB_API}/get_unread_messages/${lastMessagesUpdate.current}?user_id=${user?.userId}`,
          method: 'GET'
        }
      }

      // setLoading(true);

      const resp = await axios.request({
        ...params,
        headers: {
          ...(params.headers || {}),
          'Access-Control-Allow-Origin': '*',
          Accept: '*/*'
        }
      })

      return resp.data;
    } catch (e) {
      console.error(e);
      // enqueueSnackbar("Erro ao buscar mensagens não lidas. Atualize a página e tente novamente!", { variant: 'error' });
    }
  }

  const updateConversation = async (conversationId, updates) => {
    setConversations(prev => {
      const updatedConversations = [...prev];
      const idx = updatedConversations.findIndex(e => e.id === conversationId);
      if (idx !== -1) {
        updatedConversations[idx] = { ...updatedConversations[idx], ...updates };
      }
      return updatedConversations;
    });

    if (updates.unread !== undefined) {
      const status = { ...conversationsReadStatus || {} };
      status[conversationId] = true;
      setConversationsReadStatus(status);
    }
  }

  const updateMessages = async () => {
    if (!user) return
    const resp = await getUnreadMessages();
    setLastMessagesPointer(Date.now() - secondsToMilliseconds(20));

    setMessages(prev => {
      const messages_ = { ...prev };
      resp.forEach(conv => {
        const m = conv.msgs.map(e => ({ ...e, createdAt: Number(e.createdAt), hour: Number(e.hour) }));
        messages_[conv.id] = (messages_[conv.id] || []).concat(m.reverse());
        messages_[conv.id] = messages_[conv.id].filter((value, index, self) => index === self.findIndex((obj) => obj.createdAt === value.createdAt)); // remove duplicates

        if (messages_[conv.id]?.length !== prev[conv.id]?.length && conv.id !== selectedConversation?.id) {
          updateConversation(conv.id, { unread: true });
        }
      })


      return messages_;
    })
  }

  const scrollToBottom = () => {
    if (simpleBarRef.current) {
      simpleBarRef.current.scrollTop = simpleBarRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    (async () => {
      if (!user) return;
      setLoading(true);
      const convs = await getActiveConversations();
      setConversations(convs);

      const messages = {};
      convs.forEach(conv => {
        const m = conv.msgs.map(e => ({ ...e, createdAt: Number(e.createdAt), hour: Number(e.hour) }));
        messages[conv.id] = m.reverse();
      })

      setMessages(messages);
      setLoading(false);

      let initialConv = searchParams.get('id');
      initialConv = convs.find(e => e.id === initialConv);

      if (initialConv && initialConv) {
        setSelectedConversation(initialConv);
      }
    })()
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    const interval = setInterval(updateMessages, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (selectedConversation)
      scrollToBottom();
  }, [selectedConversation, simpleBarRef])

  useEffect(() => {
    scrollToBottom();
  }, [messages])

  return (
    <Page title="Chat">
      {loading ?
        <CircularProgress />
        :
        <Grid container flexWrap={{ xs: 'wrap', md: 'nowrap' }} sx={{ pb: 6 }}>
          <Grid item xs={12} md={4}>
            <Stack direction="column">
              {conversations.map((conv) => (
                <Box key={conv.id}>
                  <Card>
                    <CardActionArea
                      onClick={() => {
                        setSelectedConversation(conv);
                        setSearchParams({ id: conv.id });
                        updateConversation(conv.id, { unread: false });
                      }}
                      sx={{
                        p: 2,
                        height: 1,
                        borderRadius: 1,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        ...(conv.id === selectedConversation?.id) && { bgcolor: theme.palette.grey[500_16] },
                        ...(conv.unread) && { bgcolor: alpha(theme.palette.success.light, 0.2) }
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between">
                        <Typography fontWeight={'bold'}>{conv.label}</Typography>
                        {messages[conv.id]?.at(-1) &&
                          <Typography variant="caption">
                            {format(messages[conv.id].at(-1).hour, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd") ?
                              format(messages[conv.id].at(-1).hour, "HH:mm")
                              : format(messages[conv.id].at(-1).hour, "dd/MM/yyyy")
                            }
                            <br />
                            {conv.unread && <Circle color="success" />}
                          </Typography>
                        }
                      </Stack>
                      <Typography variant="caption" noWrap sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* {messages[conv.id] && !messages[conv.id].at(-1)?.author?.startsWith('whatsapp') &&
                          (conv._participants?.get(conv.attributes?.clientId)?.lastReadMessageIndex ?? -1) >= messages[conv.id].at(-1)?.index &&
                          <DoneAll />
                        } */}
                        {messages[conv.id]?.at(-1)?.msg}
                      </Typography>
                    </CardActionArea>
                  </Card>

                  <Divider variant="middle" flexItem sx={{ borderWidth: '1px' }} />
                </Box>
              ))}
            </Stack>
          </Grid>

          <Divider orientation="vertical" variant='middle' flexItem sx={{ mx: 0.5 }} />

          <Grid item xs={12} md={8}>
            {selectedConversation &&
              <Card
                sx={{
                  p: 2,
                  pr: 0,
                  '& .simplebar-scrollbar': {
                    '&:before': {
                      backgroundColor: alpha(theme.palette.grey[600], 0.48),
                    },
                    '&.simplebar-visible:before': {
                      opacity: 1,
                    },
                  },
                }}
              >
                <Card sx={{ p: 2, mb: 1, bgcolor: alpha(theme.palette.background.default, 0.1) }}>
                  <Typography variant="h5">
                    {selectedConversation.label}
                  </Typography>

                  <Typography variant="body1">
                    Status: <b>{selectedConversation.status}</b>
                  </Typography>

                  <Typography variant="body1">
                    Total: <b>{toCurrency(Number(selectedConversation.total))}</b>
                  </Typography>
                </Card>

                <Typography variant="h6" sx={{ mb: 1 }}>Mensagens</Typography>

                {/* <SimpleBar ref={simpleBarRef} style={{ maxHeight: '65vh', paddingRight: theme.spacing(2) }}> */}
                <Box ref={simpleBarRef} sx={{ maxHeight: '50vh', paddingRight: theme.spacing(2), overflowY: 'auto' }}>
                  {(() => {
                    let lastDate = null
                    return messages[selectedConversation.id].map((msg) => {
                      const date = !lastDate || (format(lastDate, "yyyy-MM-dd") < format(msg.hour, "yyyy-MM-dd")) ? format(msg.hour, "dd/MM") : null;
                      lastDate = msg.hour;
                      return (
                        <Box key={msg.createdAt} sx={{ display: 'flex', flexDirection: 'column' }}>
                          {date && <Chip size='small' label={date} sx={{ fontSize: 11, mx: 'auto', my: 1 }} />}
                          <Message
                            message={msg}
                            isClient={msg.author === 'customer'}
                            lastReadMessageIndex={1}
                          // lastReadMessageIndex={selectedConversation._participants.get(selectedConversation.attributes?.clientId)?.lastReadMessageIndex}
                          />
                        </Box>
                      )
                    })
                  })()}
                </Box>
                {/* </SimpleBar> */}

                <SendBar conversation={selectedConversation} setMessages={setMessages} scrollToBottom={scrollToBottom} />
              </Card>
            }
          </Grid>
        </Grid>
      }
    </Page>
  )
}